import { z } from 'zod'

/** Um erro de campo ou de regra, já normalizado. `field` vem sem o ponto inicial do ModelState (".Email" -> "Email"). */
export const ApiErrorDetailSchema = z.object({
    type: z.string(),
    field: z.string().nullish(),
    message: z.string(),
})

/** Erro de qualquer chamada à API depois de passar pelo `handleApiError`. */
export const ApiErrorSchema = z.object({
    statusCode: z.number(),
    message: z.string(),
    errors: z.array(ApiErrorDetailSchema),
    /** Erro semântico da Glofi (`customerError`, `addressError`, `outStockError`, `cartNotFound`...). */
    statusError: z.string().nullish(),
    title: z.string().nullish(),
    traceId: z.string().nullish(),
    /** Corpo cru da resposta, para campos próprios de cada endpoint (`attempts`, `cart`, `available`). */
    data: z.unknown().optional(),
})

export type ApiError = z.infer<typeof ApiErrorSchema>
export type IApiErrorDetail = z.infer<typeof ApiErrorDetailSchema>

// ===== Formatos de erro que a API da Glofi devolve =====
// Cada campo é lido por conta própria (`.catch(undefined)`): um campo num formato inesperado (ex.: `message` em lista)
// não pode descartar o envelope inteiro.

const optionalText = z.string().nullish().catch(undefined)

/**
 * Envelope comum: `{ message }`, `{ statusError, message }`, `{ message, errors }`, `{ message, error }`, o ProblemDetails
 * do ASP.NET e o 500 do GlobalExceptionHandler (`{ statusError, message, requestId }`). `message` também chega como
 * lista (`BadRequest(new { message = customer.ErrorList })` em create-address e delete-address) e como `messsage`
 * (grafia de alguns endpoints de customer).
 */
export const GlofiErrorEnvelopeSchema = z.looseObject({
    message: z
        .union([z.string(), z.array(z.string())])
        .nullish()
        .catch(undefined),
    messsage: optionalText,
    error: optionalText,
    statusError: optionalText,
    title: optionalText,
    traceId: optionalText,
    requestId: optionalText,
    errors: z.unknown().optional(),
})

/** Entrada do ModelState serializado (`BadRequest(new { errors = ModelState })`). */
export const ModelStateEntrySchema = z.looseObject({
    errors: z.array(z.looseObject({ errorMessage: z.string() })),
})

/**
 * Valor de cada campo do dicionário `errors`: texto (`ValidationErrors()`), lista de textos (ValidationProblemDetails)
 * ou entrada do ModelState. Lido entrada por entrada: uma entrada inesperada é ignorada, as outras ficam.
 */
export const ErrorDictionaryValueSchema = z.union([z.string(), z.array(z.string()), ModelStateEntrySchema])

export function isApiError(error: unknown): error is ApiError {
    if (!error || typeof error !== 'object') return false
    const e = error as Record<string, unknown>
    return typeof e.statusCode === 'number' && typeof e.message === 'string' && Array.isArray(e.errors)
}

/** `true` quando o erro é o `statusError` informado (ex.: `hasStatusError(error, 'outStockError')`). */
export function hasStatusError(error: unknown, statusError: string): boolean {
    return isApiError(error) && error.statusError === statusError
}

/**
 * `statusError` do erro de contrato: a API respondeu 2xx (a operação pode ter acontecido no servidor), mas o corpo não
 * passou no schema. O corpo cru fica em `data`; quem chama decide se a operação valeu (ex.: o finish lê o número do
 * pedido do corpo cru) ou revalida a fonte.
 */
export const CONTRACT_ERROR = 'contractError'

export function isContractError(error: unknown): error is ApiError {
    return hasStatusError(error, CONTRACT_ERROR)
}
